(() => {
  const hubStorageKey = 'mywaste-production-hub';
  const validHubs = new Set(['Johannesburg', 'Cape Town']);

  const readStoredHub = () => {
    try {
      return window.localStorage.getItem(hubStorageKey);
    } catch (_error) {
      return null;
    }
  };

  const writeStoredHub = (value) => {
    try {
      window.localStorage.setItem(hubStorageKey, value);
    } catch (_error) {
      // The cart attribute keeps the choice when local storage is unavailable.
    }
  };

  const updateHubLabels = (value) => {
    document.querySelectorAll('[data-current-hub]').forEach((node) => {
      node.textContent = value;
    });
  };

  const saveHubToCart = async (dialog, hub) => {
    const status = dialog.querySelector('[data-route-status]');
    if (status) status.textContent = 'Saving branch';

    try {
      const rootPath = window.Shopify?.routes?.root || '/';
      const response = await fetch(`${rootPath}cart/update.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          attributes: {
            __production_hub: hub,
            __shopify_market: dialog.dataset.currentMarket || 'default',
          },
        }),
      });

      if (!response.ok) throw new Error('Cart update failed');
      if (status) status.textContent = 'Branch saved';
    } catch (_error) {
      if (status) status.textContent = 'Branch could not be saved';
    }
  };

  const initializeLocation = (dialog) => {
    if (dialog.dataset.ready === 'true') return;
    dialog.dataset.ready = 'true';

    const countryCode = dialog.dataset.currentCountryCode;
    const countryName = dialog.dataset.currentCountryName || 'Current market';
    const closeButton = dialog.querySelector('[data-location-close]');
    const options = [...dialog.querySelectorAll('[data-location-option]')];
    let selectedHub = validHubs.has(dialog.dataset.initialHub) ? dialog.dataset.initialHub : readStoredHub();

    if (!validHubs.has(selectedHub)) selectedHub = null;

    const syncSelection = (hub) => {
      selectedHub = hub;
      writeStoredHub(hub);
      updateHubLabels(hub);
      document.dispatchEvent(new CustomEvent('hub:change', { detail: { hub } }));
      dialog.dataset.selectionRequired = 'false';
      closeButton.hidden = false;
      options.forEach((option) => {
        option.setAttribute('aria-selected', String(option.dataset.locationOption === hub));
      });
    };

    if (countryCode !== 'ZA') {
      updateHubLabels(countryName);
      dialog.dataset.selectionRequired = 'false';
      closeButton.hidden = false;
    } else if (selectedHub) {
      syncSelection(selectedHub);
    } else {
      dialog.dataset.selectionRequired = 'true';
      closeButton.hidden = true;
      window.requestAnimationFrame(() => dialog.showModal());
    }

    document.querySelectorAll('[data-location-open]').forEach((button) => {
      button.addEventListener('click', () => {
        if (!dialog.open) dialog.showModal();
      });
    });

    closeButton.addEventListener('click', () => {
      if (dialog.dataset.selectionRequired !== 'true') dialog.close();
    });

    dialog.addEventListener('cancel', (event) => {
      if (dialog.dataset.selectionRequired === 'true') event.preventDefault();
    });

    dialog.addEventListener('close', () => {
      if (countryCode !== 'ZA' || selectedHub) return;

      dialog.dataset.selectionRequired = 'true';
      window.queueMicrotask(() => {
        if (!dialog.open) dialog.showModal();
      });
    });

    options.forEach((option) => {
      option.addEventListener('click', () => {
        const hub = option.dataset.locationOption;
        if (!validHubs.has(hub)) return;
        syncSelection(hub);
        saveHubToCart(dialog, hub);
        dialog.close();
      });
    });
  };

  const closeCustomSelect = (root, returnFocus = false) => {
    const trigger = root.querySelector('[data-custom-select-trigger]');
    const menu = root.querySelector('[data-custom-select-menu]');
    root.dataset.open = 'false';
    trigger?.setAttribute('aria-expanded', 'false');
    if (menu) menu.hidden = true;
    if (returnFocus) trigger?.focus();
  };

  const openCustomSelect = (root, focusOption = false) => {
    document.querySelectorAll('[data-custom-select][data-open="true"]').forEach((other) => {
      if (other !== root) closeCustomSelect(other);
    });

    const trigger = root.querySelector('[data-custom-select-trigger]');
    const menu = root.querySelector('[data-custom-select-menu]');
    root.dataset.open = 'true';
    trigger?.setAttribute('aria-expanded', 'true');
    if (menu) menu.hidden = false;
    if (focusOption) menu?.querySelector('[data-custom-select-option]')?.focus();
  };

  const initializeCustomSelect = (root) => {
    if (root.dataset.customSelectReady === 'true') return;
    root.dataset.customSelectReady = 'true';

    const trigger = root.querySelector('[data-custom-select-trigger]');
    const value = root.querySelector('[data-custom-select-value]');
    const input = root.querySelector('[data-custom-select-input]');
    const options = [...root.querySelectorAll('[data-custom-select-option]')];
    const error = root.querySelector('[data-custom-select-error]');

    trigger?.addEventListener('click', () => {
      if (root.dataset.open === 'true') {
        closeCustomSelect(root);
      } else {
        openCustomSelect(root);
      }
    });

    trigger?.addEventListener('keydown', (event) => {
      if (event.key === 'ArrowDown') {
        event.preventDefault();
        openCustomSelect(root, true);
      }
    });

    options.forEach((option, index) => {
      option.addEventListener('click', () => {
        const label = option.dataset.label || option.textContent.trim().replace(/\s+/g, ' ');
        if (input) input.value = option.dataset.value || '';
        if (value) value.textContent = label;
        if (error) error.hidden = true;

        options.forEach((item) => {
          item.setAttribute('aria-selected', String(item === option));
        });

        closeCustomSelect(root, true);
        root.dispatchEvent(new CustomEvent('custom-select:change', {
          detail: {
            value: option.dataset.value,
            label,
            price: option.dataset.price,
            available: option.dataset.available,
          },
        }));
      });

      option.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') {
          event.preventDefault();
          closeCustomSelect(root, true);
        }
        if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
          event.preventDefault();
          const direction = event.key === 'ArrowDown' ? 1 : -1;
          options[(index + direction + options.length) % options.length].focus();
        }
      });
    });

    document.addEventListener('click', (event) => {
      if (!root.contains(event.target)) closeCustomSelect(root);
    });

    if (root.hasAttribute('data-custom-select-required')) {
      root.closest('form')?.addEventListener('submit', (event) => {
        if (input?.value) return;
        event.preventDefault();
        if (error) error.hidden = false;
        trigger?.focus();
      });
    }
  };

  const initializeGallery = (root) => {
    if (root.dataset.galleryReady === 'true') return;
    root.dataset.galleryReady = 'true';

    const slides = [...root.querySelectorAll('[data-gallery-slide]')];
    const thumbnails = [...root.querySelectorAll('[data-gallery-thumbnail]')];
    const current = root.querySelector('[data-gallery-current]');
    let index = 0;

    const show = (nextIndex) => {
      if (!slides.length) return;
      index = (nextIndex + slides.length) % slides.length;
      slides.forEach((slide, slideIndex) => {
        slide.hidden = slideIndex !== index;
      });
      thumbnails.forEach((thumbnail, thumbnailIndex) => {
        if (thumbnailIndex === index) {
          thumbnail.setAttribute('aria-current', 'true');
        } else {
          thumbnail.removeAttribute('aria-current');
        }
      });
      if (current) current.textContent = String(index + 1);
    };

    root.querySelector('[data-gallery-previous]')?.addEventListener('click', () => show(index - 1));
    root.querySelector('[data-gallery-next]')?.addEventListener('click', () => show(index + 1));
    thumbnails.forEach((thumbnail) => {
      thumbnail.addEventListener('click', () => show(Number(thumbnail.dataset.galleryThumbnail)));
    });
    root.addEventListener('product-gallery:show-media', (event) => {
      const mediaId = String(event.detail?.mediaId || '');
      const slideIndex = slides.findIndex((slide) => slide.dataset.mediaId === mediaId);
      if (slideIndex !== -1) show(slideIndex);
    });
  };

  const initializeFilter = (root) => {
    if (root.dataset.filterReady === 'true') return;
    root.dataset.filterReady = 'true';

    const input = root.querySelector('[data-product-filter]');
    const items = [...root.querySelectorAll('[data-filter-item]')];
    const count = root.querySelector('[data-filter-count]');
    const empty = root.querySelector('[data-filter-empty]');
    const pagination = root.querySelector('[data-filter-pagination]');

    input?.addEventListener('input', () => {
      const term = input.value.trim().toLowerCase();
      let visible = 0;

      items.forEach((item) => {
        const matches = !term || item.dataset.filterText.includes(term);
        item.hidden = !matches;
        if (matches) visible += 1;
      });

      if (count) count.textContent = String(visible);
      if (empty) empty.hidden = visible !== 0;
      if (pagination) pagination.hidden = Boolean(term);
    });
  };

  const initializeHeroDeck = (root) => {
    if (root.dataset.heroDeckReady === 'true') return;
    root.dataset.heroDeckReady = 'true';

    const cards = [...root.querySelectorAll('[data-hero-card]')];
    const controls = [...root.querySelectorAll('[data-hero-control]')];
    const toggle = root.querySelector('[data-hero-toggle]');
    const pauseIcon = root.querySelector('[data-pause-icon]');
    const playIcon = root.querySelector('[data-play-icon]');
    const status = root.querySelector('[data-hero-status]');
    const interval = Math.max(Number(root.dataset.interval) || 6000, 3000);
    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    let activeIndex = 0;
    let timer = null;
    let pausedByUser = prefersReducedMotion;
    let pointerInside = false;
    let focusInside = false;

    const isPaused = () => pausedByUser || pointerInside || focusInside || document.hidden;

    const syncToggle = () => {
      if (!toggle) return;
      toggle.setAttribute('aria-label', pausedByUser ? root.dataset.playLabel : root.dataset.pauseLabel);
      root.dataset.paused = String(pausedByUser);
      if (pauseIcon) pauseIcon.hidden = pausedByUser;
      if (playIcon) playIcon.hidden = !pausedByUser;
    };

    const schedule = () => {
      window.clearTimeout(timer);
      if (cards.length < 2 || isPaused()) return;
      timer = window.setTimeout(() => {
        showCard(activeIndex + 1, false);
      }, interval);
    };

    const showCard = (nextIndex, announce = true) => {
      activeIndex = (nextIndex + cards.length) % cards.length;
      cards.forEach((card, index) => {
        const position = (index - activeIndex + cards.length) % cards.length;
        const isActive = index === activeIndex;
        card.dataset.position = String(position);
        card.setAttribute('aria-hidden', String(!isActive));
        card.tabIndex = isActive ? 0 : -1;
      });
      controls.forEach((control, index) => {
        if (index === activeIndex) {
          control.setAttribute('aria-current', 'true');
        } else {
          control.removeAttribute('aria-current');
        }
      });
      if (announce && status) status.textContent = cards[activeIndex]?.dataset.title || '';
      schedule();
    };

    controls.forEach((control) => {
      control.addEventListener('click', () => showCard(Number(control.dataset.heroControl)));
    });

    toggle?.addEventListener('click', () => {
      pausedByUser = !pausedByUser;
      syncToggle();
      schedule();
    });

    root.addEventListener('pointerenter', () => {
      pointerInside = true;
      schedule();
    });
    root.addEventListener('pointerleave', () => {
      pointerInside = false;
      schedule();
    });
    root.addEventListener('focusin', () => {
      focusInside = true;
      schedule();
    });
    root.addEventListener('focusout', (event) => {
      if (root.contains(event.relatedTarget)) return;
      focusInside = false;
      schedule();
    });
    document.addEventListener('visibilitychange', schedule);

    syncToggle();
    if (cards.length) showCard(0, false);
  };

  const initializeProduct = (root) => {
    if (root.dataset.productReady === 'true') return;
    root.dataset.productReady = 'true';

    const picker = root.querySelector('[data-variant-picker]');
    const variantInput = root.querySelector('[data-variant-id]');
    const optionGroups = [...root.querySelectorAll('[data-variant-option]')];
    const variants = [...root.querySelectorAll('[data-product-variant]')].map((node) => ({
      id: node.dataset.id,
      options: JSON.parse(node.dataset.options || '[]'),
      price: node.dataset.price,
      sku: node.dataset.sku,
      available: node.dataset.available === 'true',
      mediaId: node.dataset.mediaId,
    }));
    const gallery = root.querySelector('[data-product-gallery]');
    const price = root.querySelector('[data-product-price]');
    const sku = root.querySelector('[data-product-sku]');
    const skuValue = root.querySelector('[data-product-sku-value]');
    const submit = root.querySelector('[data-add-to-cart]');

    const selectedOptions = () => optionGroups.map((group) => {
      const selected = group.querySelector('[data-option-value]:checked');
      return selected?.value || '';
    });

    const syncVariant = () => {
      const selected = selectedOptions();
      const variant = variants.find((item) => item.options.every((value, index) => value === selected[index]));

      optionGroups.forEach((group, optionIndex) => {
        const selectedLabel = group.querySelector('[data-selected-option]');
        if (selectedLabel) selectedLabel.textContent = selected[optionIndex];

        group.querySelectorAll('[data-option-value]').forEach((input) => {
          const candidate = [...selected];
          candidate[optionIndex] = input.value;
          // Existing variants remain selectable so customers can inspect their
          // price and imagery even when stock is exhausted.
          const exists = variants.some((item) => (
            item.options.every((value, index) => value === candidate[index])
          ));
          input.disabled = !exists && !input.checked;
        });
      });

      if (variantInput) variantInput.value = variant?.id || '';
      if (price && variant?.price) {
        price.dataset.hubVariant = variant.id;
        price.dataset.basePrice = variant.price;
        price.textContent = variant.price;
        document.dispatchEvent(new CustomEvent('hub:variant-change'));
      }
      if (sku && skuValue) {
        skuValue.textContent = variant?.sku || '';
        sku.hidden = !variant?.sku;
      }
      if (submit) {
        submit.disabled = !variant?.available;
        submit.textContent = submit.disabled ? 'Sold out' : 'Add to cart';
      }
      if (gallery && variant?.mediaId) {
        gallery.dispatchEvent(new CustomEvent('product-gallery:show-media', {
          detail: { mediaId: variant.mediaId },
        }));
      }

      if (variant?.id) {
        const url = new URL(window.location.href);
        url.searchParams.set('variant', variant.id);
        window.history.replaceState({}, '', url);
      }
    };

    picker?.addEventListener('change', syncVariant);
    if (picker) syncVariant();
  };

  let googleMapsPromise = null;

  const loadGooglePlaces = (apiKey) => {
    if (window.google?.maps?.importLibrary) return Promise.resolve();
    if (googleMapsPromise) return googleMapsPromise;

    googleMapsPromise = new Promise((resolve, reject) => {
      const callbackName = '__mywasteGoogleMapsReady';
      const script = document.createElement('script');

      window[callbackName] = () => {
        delete window[callbackName];
        resolve();
      };

      script.src = `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(apiKey)}&loading=async&libraries=places&callback=${callbackName}&v=weekly`;
      script.async = true;
      script.onerror = () => {
        delete window[callbackName];
        googleMapsPromise = null;
        reject(new Error('Google Maps could not be loaded'));
      };
      document.head.append(script);
    });

    return googleMapsPromise;
  };

  const setAddressField = (form, name, value) => {
    const input = form.querySelector(`[data-address-field="${name}"]`);
    if (!input) return;

    input.value = value || '';
    input.dispatchEvent(new Event('input', { bubbles: true }));
    input.dispatchEvent(new Event('change', { bubbles: true }));
  };

  const initializeAddressAutocomplete = async (root) => {
    if (root.dataset.addressAutocompleteReady === 'true' || root.dataset.addressAutocompleteReady === 'loading') return;
    root.dataset.addressAutocompleteReady = 'loading';

    const apiKey = root.dataset.googleMapsKey;
    const control = root.querySelector('[data-address-autocomplete-control]');
    const status = root.querySelector('[data-address-autocomplete-status]');
    const form = root.closest('form');

    const setStatus = (message, state = '') => {
      if (!status) return;
      status.textContent = message;
      status.dataset.state = state;
      status.hidden = !message;
    };

    if (!apiKey || !control || !form) {
      root.dataset.addressAutocompleteReady = 'error';
      if (control) control.hidden = true;
      setStatus('Address search is unavailable. Enter the address below.', 'error');
      return;
    }

    try {
      await loadGooglePlaces(apiKey);
      const { PlaceAutocompleteElement } = await window.google.maps.importLibrary('places');
      const autocomplete = new PlaceAutocompleteElement();

      autocomplete.includedRegionCodes = ['za'];
      autocomplete.setAttribute('aria-label', 'Search delivery address');
      autocomplete.placeholder = 'Start typing a street address';
      control.replaceChildren(autocomplete);
      root.dataset.addressAutocompleteReady = 'true';
      setStatus('Start typing and select an address. We will fill the delivery fields below.');

      autocomplete.addEventListener('gmp-select', async (event) => {
        const placePrediction = event.placePrediction;
        if (!placePrediction) return;

        setStatus('Filling the address');

        try {
          const place = placePrediction.toPlace();
          await place.fetchFields({ fields: ['addressComponents', 'formattedAddress'] });
          const components = place.addressComponents || [];
          const getComponent = (type) => components.find((component) => component.types.includes(type))?.longText || '';
          const streetNumber = getComponent('street_number');
          const route = getComponent('route');
          const unit = getComponent('subpremise');
          const streetBase = [streetNumber, route].filter(Boolean).join(' ');
          const street = [unit ? `Unit ${unit}` : '', streetBase].filter(Boolean).join(', ') || place.formattedAddress?.split(',')[0] || '';
          const city = getComponent('locality') || getComponent('postal_town') || getComponent('sublocality_level_1') || getComponent('administrative_area_level_2');

          setAddressField(form, 'street', street);
          setAddressField(form, 'city', city);
          setAddressField(form, 'province', getComponent('administrative_area_level_1'));
          setAddressField(form, 'postal-code', getComponent('postal_code'));
          setAddressField(form, 'formatted', place.formattedAddress || '');
          setStatus('');
        } catch (_error) {
          setStatus('We could not fill this address. Enter the address below.', 'error');
        }
      });
    } catch (_error) {
      root.dataset.addressAutocompleteReady = 'error';
      control.hidden = true;
      setStatus('Address search is unavailable. Enter the address below.', 'error');
    }
  };

  const initializeFulfilment = (root) => {
    if (root.dataset.fulfilmentReady === 'true') return;
    root.dataset.fulfilmentReady = 'true';

    const choices = [...root.querySelectorAll('[data-fulfilment-choice]')];
    const panels = [...root.querySelectorAll('[data-fulfilment-panel]')];
    const deliverySucceeded = Boolean(root.querySelector('[data-delivery-success]'));

    const showPanel = (value) => {
      choices.forEach((choice) => {
        choice.checked = choice.value === value;
      });
      panels.forEach((panel) => {
        panel.hidden = panel.dataset.fulfilmentPanel !== value;
      });

      if (value === 'delivery') {
        root.querySelectorAll('[data-address-autocomplete]').forEach(initializeAddressAutocomplete);
      }
    };

    choices.forEach((choice) => {
      choice.addEventListener('change', () => showPanel(choice.value));
    });

    showPanel(deliverySucceeded ? 'delivery' : 'pickup');
  };

  const initializeDeliveryRequest = (form) => {
    if (form.dataset.deliveryRequestReady === 'true') return;
    form.dataset.deliveryRequestReady = 'true';

    const error = form.querySelector('[data-delivery-form-error]');
    const submit = form.querySelector('[data-delivery-submit]');
    let requestId = window.crypto.randomUUID();

    const showSuccess = () => {
      const success = document.createElement('div');
      success.className = 'delivery-form__success';
      success.dataset.deliverySuccess = '';
      success.setAttribute('role', 'status');
      success.innerHTML = '<strong>Request submitted</strong><span>You’ll receive your delivery quote within 24–48 hours.</span>';
      form.replaceWith(success);
    };

    form.addEventListener('submit', async (event) => {
      event.preventDefault();
      if (!form.reportValidity()) return;

      const formData = new FormData(form);
      const offloading = String(formData.get('offloading') || '');
      if (!offloading) return;

      if (error) error.hidden = true;
      if (submit) {
        submit.disabled = true;
        submit.setAttribute('aria-busy', 'true');
        submit.textContent = 'Submitting request…';
      }

      const rootPath = window.Shopify?.routes?.root || '/';
      let cartData;
      try {
        cartData = await window.MyWasteDeliveryRequest.resolveCartData({
          fetchImplementation: window.fetch.bind(window),
          productionHub: readStoredHub(),
          rootPath,
        });
      } catch (cartDataError) {
        if (error) {
          error.textContent = cartDataError?.code === 'production_hub_missing'
            ? 'Choose your production hub before requesting delivery.'
            : cartDataError.message || 'Your cart details could not be read. Refresh the page and try again.';
          error.hidden = false;
          error.focus();
        }
        if (submit) {
          submit.disabled = false;
          submit.removeAttribute('aria-busy');
          submit.textContent = 'Request delivery quote';
        }
        return;
      }

      const successStorageKey = `mywaste-delivery-request:${cartData.cartToken}`;

      const payload = {
        requestId,
        cartToken: cartData.cartToken,
        lineItems: cartData.lineItems,
        productionHub: cartData.productionHub,
        expectedUnitPrices: cartData.expectedUnitPrices,
        fulfilment: 'delivery',
        contact: {
          name: String(formData.get('name') || ''),
          email: String(formData.get('email') || ''),
          phone: String(formData.get('phone') || ''),
        },
        address: {
          address1: String(formData.get('address1') || ''),
          city: String(formData.get('city') || ''),
          province: String(formData.get('province') || ''),
          postalCode: String(formData.get('postalCode') || ''),
          formattedAddress: String(formData.get('formattedAddress') || ''),
        },
        offloading,
        notes: String(formData.get('notes') || ''),
        website: String(formData.get('website') || ''),
      };

      let submitted = false;
      try {
        const fingerprint = JSON.stringify({ ...payload, requestId: undefined });
        const attemptKey = 'mywaste-delivery-attempt';
        let attempt;
        try { attempt = JSON.parse(window.sessionStorage.getItem(attemptKey)); } catch {}
        if (attempt?.fingerprint === fingerprint) requestId = attempt.requestId;
        payload.requestId = requestId;
        window.sessionStorage.setItem(attemptKey, JSON.stringify({ fingerprint, requestId }));
        const response = await fetch(form.action, {
          method: 'POST',
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(payload),
        });
        const result = await response.json().catch(() => null);
        if (!response.ok || !result?.ok) throw new Error(result?.message || 'Delivery request failed');
        submitted = true;

        const clearResponse = await fetch(`${rootPath}cart/clear.js`, {
          method: 'POST',
          headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
          body: JSON.stringify({}),
        });
        if (!clearResponse.ok) throw new Error('Cart clear failed');

        try {
          if (successStorageKey) window.sessionStorage.setItem(successStorageKey, payload.requestId);
        } catch (_error) {
          // The draft order and cart clear have already completed, so storage failure is non-fatal.
        }
        showSuccess();
      } catch (failure) {
        if (error) {
          error.textContent = submitted
            ? 'Your delivery request was submitted, but the cart could not be cleared. Do not submit it again.'
            : failure.message || 'We could not finish your request. Please try again.';
          error.hidden = false;
          error.focus();
        }
        if (submit) {
          submit.disabled = submitted;
          submit.removeAttribute('aria-busy');
          submit.textContent = 'Request delivery quote';
        }
      }
    });
  };

  const initializeMobileNav = (root) => {
    if (root.dataset.mobileNavReady === 'true') return;
    root.dataset.mobileNavReady = 'true';

    const summary = root.querySelector('summary');
    const syncLabel = () => summary?.setAttribute('aria-label', root.open ? 'Close menu' : 'Open menu');
    const close = () => {
      root.open = false;
      syncLabel();
    };

    root.addEventListener('toggle', syncLabel);
    root.querySelectorAll('a, [data-location-open]').forEach((control) => {
      control.addEventListener('click', close);
    });
    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && root.open) {
        close();
        summary?.focus();
      }
    });

    syncLabel();
  };

  const initialize = (scope = document) => {
    scope.querySelectorAll('[data-location-dialog]').forEach(initializeLocation);
    scope.querySelectorAll('[data-custom-select]').forEach(initializeCustomSelect);
    scope.querySelectorAll('[data-product-gallery]').forEach(initializeGallery);
    scope.querySelectorAll('[data-filter-root]').forEach(initializeFilter);
    scope.querySelectorAll('[data-hero-deck]').forEach(initializeHeroDeck);
    scope.querySelectorAll('[data-product-root]').forEach(initializeProduct);
    scope.querySelectorAll('[data-delivery-request-form]').forEach(initializeDeliveryRequest);
    scope.querySelectorAll('[data-fulfilment-root]').forEach(initializeFulfilment);
    scope.querySelectorAll('.mobile-nav').forEach(initializeMobileNav);
  };

  initialize();
  document.addEventListener('shopify:section:load', (event) => initialize(event.target));
})();
